'use client';

import { useState } from 'react';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import type { NumberFormatType } from '@/lib/platform/utils/locale-formatting';
import { useCurrency } from '@/lib/platform/contexts/currency-context';
import { getCurrencySymbol } from '@/lib/platform/utils/currency';
import SocialDeepDiveModal from './SocialDeepDiveModal';
import type {
  TotalMetrics,
  PlatformMetrics,
} from '@/lib/products/valuations/social-deepdive/types';

interface SocialDeepDiveTriggerProps {
  valuationId: string | null;
  organizationId: string;
  hasData: boolean;
  currentTotals: TotalMetrics;
  onSave: (totals: TotalMetrics, platformMetrics: Record<string, PlatformMetrics>) => void;
  existingPlatformMetrics?: Record<string, PlatformMetrics>;
  disabled?: boolean;
  numberFormat?: NumberFormatType;
}

export default function SocialDeepDiveTrigger({
  valuationId,
  organizationId,
  hasData,
  currentTotals,
  onSave,
  existingPlatformMetrics,
  disabled = false,
  numberFormat = 'eu',
}: SocialDeepDiveTriggerProps) {
  const { darkMode } = useDarkMode();
  const { baseCurrency } = useCurrency();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Display totals from valuations table (MASTER data) - ALWAYS show these values
  // Platform breakdown (valuation_social_platforms) is only used INSIDE the modal

  return (
    <>
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        disabled={disabled}
        className={`w-full rounded-lg border p-4 text-left transition-all ${
          darkMode
            ? 'border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10'
            : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50'
        } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
      >
        <div className="mb-1 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ChartBarIcon className="h-5 w-5 text-indigo-500" />
            <h4 className={`font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
              Platform Breakdown
            </h4>
          </div>
          <span
            className={`rounded px-2 py-0.5 text-xs font-medium ${
              darkMode ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-700'
            }`}
          >
            Plugin
          </span>
        </div>

        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Use <span className="font-medium">SocialDeepDive</span> to split your social media data
          across platforms like Instagram, Facebook, YouTube, TikTok, and more.
        </p>

        {/* Display totals from valuations table (MASTER data) - ALWAYS show if hasData */}
        {hasData && (
          <div className="mt-4 grid grid-cols-3 gap-4">
            {/* Total Followers */}
            {currentTotals.followers > 0 && (
              <div>
                <div
                  className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                >
                  Total Followers
                </div>
                <div
                  className={`text-lg font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}
                >
                  {currentTotals.followers.toLocaleString()}
                </div>
                <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  Avg CPM: {getCurrencySymbol(baseCurrency)}
                  {currentTotals.followers_cpm.toFixed(4)}
                </div>
              </div>
            )}

            {/* Total Impressions */}
            {currentTotals.impressions > 0 && (
              <div>
                <div
                  className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                >
                  Total Impressions
                </div>
                <div
                  className={`text-lg font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}
                >
                  {currentTotals.impressions.toLocaleString()}
                </div>
                <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  Avg CPM: {getCurrencySymbol(baseCurrency)}
                  {currentTotals.impressions_cpm.toFixed(4)}
                </div>
              </div>
            )}

            {/* Total Engagements */}
            {currentTotals.engagements > 0 && (
              <div>
                <div
                  className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                >
                  Total Engagements
                </div>
                <div
                  className={`text-lg font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}
                >
                  {currentTotals.engagements.toLocaleString()}
                </div>
                <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  Avg CPM: {getCurrencySymbol(baseCurrency)}
                  {currentTotals.engagements_cpm.toFixed(4)}
                </div>
              </div>
            )}
          </div>
        )}

        <div className={`mt-4 border-t pt-3 ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
          <span
            className={`text-sm font-medium ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}
          >
            Add Breakdown
          </span>
        </div>
      </button>

      <SocialDeepDiveModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={onSave}
        valuationId={valuationId}
        organizationId={organizationId}
        existingPlatformMetrics={existingPlatformMetrics}
        currentTotals={currentTotals}
        numberFormat={numberFormat}
      />
    </>
  );
}
